import { useState, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { handleGetRequest, handlePatchRequest } from '../../utils/api-handler.util'
import { showToaster } from '../../utils/show-toaster.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import InputField from '../common/InputField'

const EditSubscriptionTierModal = ({ isOpen, onClose }) => {
  const [tierPrices, setTierPrices] = useState({})

  const { data: tiers = [], isLoading } = useQuery({
    queryKey: QueryKeys.adminSubscriptionTiers(),
    queryFn: async () => {
      const res = await handleGetRequest('/subscription-tiers')
      return res.data
    },
    enabled: isOpen,
  })

  // Initialize tier prices when data is loaded
  useEffect(() => {
    if (tiers.length > 0) {
      const initialPrices = {}
      tiers.forEach((tier) => {
        initialPrices[tier.id] = tier.price
      })
      setTierPrices(initialPrices)
    }
  }, [tiers])

  const saveChangesMutation = useMutation({
    mutationFn: async (changes) => {
      const promises = changes.map(({ subscriptionId, price }) =>
        handlePatchRequest(`/admin/subscriptions/${subscriptionId}`, {
          price: parseFloat(price),
        })
      )
      return Promise.all(promises)
    },
    onSuccess: () => {
      showToaster('success', 'Subscription tier prices updated successfully')
      QueryKeys.adminSubscriptionTiers.invalidate()
      handleClose()
    },
    onError: () => {
      showToaster('error', 'Failed to update subscription tier prices')
    },
  })

  const handlePriceChange = (tierId, newPrice) => {
    setTierPrices((prev) => ({
      ...prev,
      [tierId]: newPrice,
    }))
  }

  const handleSave = () => {
    const changes = tiers
      .filter((tier) => parseFloat(tierPrices[tier.id]) !== parseFloat(tier.price))
      .map((tier) => ({
        subscriptionId: tier.id,
        price: tierPrices[tier.id],
      }))

    if (changes.length === 0) return

    saveChangesMutation.mutate(changes)
  }

  const handleClose = () => {
    onClose()
    setTierPrices({})
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-background rounded-2xl shadow-xl w-full max-w-2xl p-6 relative">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Edit Subscription Tiers</h2>
          <button
            className="text-gray-400 hover:text-gray-600 transition-colors"
            onClick={handleClose}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {tiers.map((tier) => (
              <div
                key={tier.id}
                className="p-4 border border-gray-200 rounded-xl hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-lg text-gray-900 mb-1">{tier.name}</h3>
                    <p className="text-sm text-gray-500">Current: {tier.priceText}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-32">
                      <InputField
                        type="number"
                        value={tierPrices[tier.id] || ''}
                        onChange={(e) => handlePriceChange(tier.id, e.target.value)}
                        placeholder="50"
                        step="1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                className="bg-secondary-600 text-white px-6 py-2 rounded-lg hover:bg-orange-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSave}
                disabled={saveChangesMutation.isLoading}
              >
                {saveChangesMutation.isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </div>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditSubscriptionTierModal
